import { useContext, useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import AppUser from "../app-user/app-user";
import Notifier from "../notifier/notifier";
import { updateUserTncStatus, TTncStatus } from "../api/tnc";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../routes";
import Utils from "../../utils/utils";
import { PortalContext } from "../../context/portal-context";
import ReactMarkdown from 'react-markdown';

type Props = {
  show: boolean;
  setShow: (v: boolean) => void;
  api_content?: string;
  api_title?: string;
};

const TEXT_FALLBACK = `Not available.`;

export default function TnCPopup({ show, setShow, api_content, api_title }: Props) {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<string>("");
  const [title, setTitle] = useState<string>("Terms and Conditions");
  const [submitting, setSubmitting] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);

  const { setPortalSetting } = useContext(PortalContext);

  const navigate = useNavigate();

  useEffect(() => {
    if (!show) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        if (mounted) {
          setContent(api_content || TEXT_FALLBACK);
          setTitle(api_title || "Terms and Conditions");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [show]);

  const submitChoice = async (statusValue: TTncStatus) => {
    try {
      setSubmitting(true);
      const user = await AppUser.getInfo();
      if (!user?.id) {
        Notifier.error("User not found. Please login again.");
        return;
      }
      const res = await updateUserTncStatus(user.id, statusValue);
      if (!res.status) {
        Notifier.error(res.message || "Failed to submit your choice");
        return;
      }
      if (statusValue === 2) {
        setPortalSetting((d) => ({...d, mobile_nav_open: false}));
        navigate(ROUTES.AUTH_LOGOUT);
        Notifier.info("You have been logged out as you did not accept the Terms and Conditions.");
      } else {
        setShow(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAgree = () => submitChoice(1);

  const handleDisagree = () => setShowConfirm(true);

  return (
    <>
      <Modal show={show} onHide={() => {}} backdrop="static" keyboard={false} centered size="lg">
        <Modal.Header closeButton={false}>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {loading ? (
            <div className="d-flex align-items-center justify-content-center py-4">
              <Spinner animation="border" role="status" className="me-2" />
              <span>Loading Terms and Conditions...</span>
            </div>
          ) : (
             <ReactMarkdown>
                {content}
             </ReactMarkdown>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleAgree} disabled={submitting || loading}>
            {submitting ? "Processing..." : "Agree"}
          </Button>
          <Button variant="danger" onClick={handleDisagree} disabled={submitting || loading}>
            {submitting ? "Processing..." : "Disagree"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>Confirm Disagreement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Do you wish to Disagree with the Terms and Conditions?
          </p>
          <p className="mb-0">
            Please note that access to GROW-Pro is permitted only to those who accept the Terms and Conditions.
            You are welcome to contact your IWSP coordinator for further enquiry.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowConfirm(false);
              submitChoice(2);
            }}
            disabled={submitting}
          >
            {submitting ? "Processing..." : "Okay"}
          </Button>
          <Button
            variant="danger"
            onClick={() => setShowConfirm(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
